import type { MyTaskDto } from '@taqon/contracts';
import { daysBetween } from '../../../shared/documents/dates';

/**
 * The caller's open tasks by when they are due (CLR-66): for My tasks, its dashboard card and the
 * daily popup. "Today" is the business date in the business time zone, so a task never changes
 * group with the browser's own zone.
 */
export const GROUPS = [
  { key: 'overdue', title: 'Overdue' },
  { key: 'today', title: 'Due today' },
  { key: 'week', title: 'Next 7 days' },
  { key: 'later', title: 'Later' },
  { key: 'none', title: 'No due date' },
] as const;
export type GroupKey = (typeof GROUPS)[number]['key'];

export const WEEK_DAYS = 7;

export function groupOf(dueDate: string | null, today: string): GroupKey {
  if (dueDate === null) return 'none';
  const days = daysBetween(today, dueDate);
  if (days < 0) return 'overdue';
  if (days === 0) return 'today';
  if (days <= WEEK_DAYS) return 'week';
  return 'later';
}

/** The tasks of each group, earliest due first; groups without tasks are left out. */
export function groupTasks(
  tasks: readonly MyTaskDto[],
  today: string,
): { key: GroupKey; title: string; tasks: MyTaskDto[] }[] {
  const sorted = [...tasks].sort(
    (a, b) =>
      (a.dueDate ?? '9999-12-31').localeCompare(b.dueDate ?? '9999-12-31') ||
      a.key.localeCompare(b.key),
  );
  return GROUPS.map((group) => ({
    key: group.key,
    title: group.title,
    tasks: sorted.filter((task) => groupOf(task.dueDate, today) === group.key),
  })).filter((group) => group.tasks.length > 0);
}

/** "3 days overdue", "1 day overdue", "Due today", "Due tomorrow", "5 days left"; null without a date. */
export function remaining(dueDate: string | null, today: string): string | null {
  if (dueDate === null) return null;
  const days = daysBetween(today, dueDate);
  if (days === 0) return 'Due today';
  if (days === 1) return 'Due tomorrow';
  if (days < 0) return `${String(-days)} ${-days === 1 ? 'day' : 'days'} overdue`;
  return `${String(days)} days left`;
}

/** The counts the dashboard shows: overdue, due today, due in the next seven days. */
export function counts(tasks: readonly MyTaskDto[], today: string) {
  const of = (key: GroupKey) => tasks.filter((t) => groupOf(t.dueDate, today) === key).length;
  return { overdue: of('overdue'), today: of('today'), week: of('week'), open: tasks.length };
}

/** Where a task opens: its project's work, with the task panel open. */
export function taskHref(task: { projectId: string; id: string }): string {
  return `/projects/${task.projectId}/work?task=${task.id}`;
}
